// ============================================================================
// 全ボス共通 DEVプロキシ — 最終絵が無くても駅1の戦闘設計を止めない仮モデル。
//   dataURL を既存スプライトキャッシュへ注入するので、面積正規化・接地・crossfade・
//   パリィ・影・撃破フェードなど、全ボス共通の描画経路をそのまま通る。
//   卒業 = public/sprites/bosses/<bossId>/ に PNG を置き、フラグは OFF のままにする。
//   全ボス既定 OFF。DEV の __bossProxy(bossId, on) でキャッシュごと切り替える。
// ============================================================================
import { AWOKEN_PROXY_BOSS_ID, getAwokenProxyDataUrl } from './awoken_king_proxy'

export type BossProxyDrawer = (key: string, facing: 'left' | 'right') => string | null

// 新ボスは駅1で固有の骨格描画をここへ足す。未登録なら下の汎用マネキンを使う。
const PROXY_DRAWERS: Record<string, BossProxyDrawer> = {
  [AWOKEN_PROXY_BOSS_ID]: (key) => getAwokenProxyDataUrl(key),
}
const enabledByBoss = new Map<string, boolean>()

export function bossProxyEnabled(bossId: string): boolean {
  return enabledByBoss.get(bossId) ?? false
}

export function setBossProxyEnabled(bossId: string, on: boolean): void {
  enabledByBoss.set(bossId, on)
}

export function getBossProxyDataUrl(bossId: string, key: string, facing: 'left' | 'right'): string | null {
  if (!bossProxyEnabled(bossId)) return null
  const drawer = Object.hasOwn(PROXY_DRAWERS, bossId) ? PROXY_DRAWERS[bossId] : drawGenericProxyDataUrl
  return drawer(key, facing)
}

const genericDataUrlCache = new Map<string, string>()

export function drawGenericProxyDataUrl(key: string, facing: 'left' | 'right'): string | null {
  if (typeof document === 'undefined') return null
  const cacheKey = `${facing}/${key}`
  const cached = genericDataUrlCache.get(cacheKey)
  if (cached) return cached
  const canvas = document.createElement('canvas')
  canvas.width = 640
  canvas.height = 900
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  // 接地 y=880。キーによらず同じ輪郭・面積を保ち、正規化による体格の跳びを防ぐ。
  ctx.fillStyle = '#6b6872'
  ctx.strokeStyle = '#3a3840'
  ctx.lineWidth = 10
  ctx.lineJoin = 'round'
  ctx.beginPath()
  ctx.arc(320, 150, 65, 0, Math.PI * 2)
  ctx.fill()
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(250, 235)
  ctx.lineTo(390, 235)
  ctx.lineTo(400, 560)
  ctx.lineTo(240, 560)
  ctx.closePath()
  ctx.fill()
  ctx.stroke()
  // 両腕・両脚は同じ太さの線。足底 (線端込み) が接地ラインに届く。
  ctx.lineCap = 'round'
  const limbs = [[250, 265, 210, 535], [390, 265, 430, 535],
    [275, 550, 260, 850], [365, 550, 380, 850]]
  for (const [x1, y1, x2, y2] of limbs) {
    ctx.beginPath()
    ctx.moveTo(x1, y1)
    ctx.lineTo(x2, y2)
    ctx.strokeStyle = '#3a3840'
    ctx.lineWidth = 60
    ctx.stroke()
    ctx.strokeStyle = '#6b6872'
    ctx.lineWidth = 40
    ctx.stroke()
  }
  // 短剣だけを向きに合わせる。文字は反転させず胴の内側に収める。
  const handX = facing === 'left' ? 210 : 430
  const tipX = facing === 'left' ? 100 : 540
  ctx.strokeStyle = '#3a3840'
  ctx.lineWidth = 18
  ctx.beginPath()
  ctx.moveTo(handX, 520)
  ctx.lineTo(tipX, 520)
  ctx.stroke()
  ctx.strokeStyle = '#aaa7af'
  ctx.lineWidth = 8
  ctx.stroke()
  ctx.fillStyle = '#dedce2'
  ctx.textAlign = 'center'
  ctx.font = '10px monospace'
  ctx.fillText(key, 320, 360, 120)
  ctx.font = '16px monospace'
  ctx.fillText('DEV', 320, 400, 120)

  const url = canvas.toDataURL('image/png')
  genericDataUrlCache.set(cacheKey, url)
  return url
}
